<template>
  <v-navigation-drawer
    v-model="drawer"
    :expand-on-hover="expandOnHover"
    :right="$vuetify.rtl"
    mobile-break-point="960"
    app
    width="280"
    v-bind="$attrs"
    class="modern-drawer"
    :mini-variant="miniVariant"
  >
    <!-- Header Section -->
    <div class="drawer-header">
      <v-list-item class="user-profile-section">
        <v-list-item-avatar size="56" class="user-avatar">
          <v-img 
            v-if="user.avatar" 
            :src="user.avatar" 
            :alt="user.user_name"
          />
          <v-img 
            v-else
            src="ovec.png" 
            :alt="user.user_name"
          />
        </v-list-item-avatar>

        <v-list-item-content v-if="!miniVariant">
          <v-list-item-title class="user-name">
            {{ user.user_name || 'ผู้ใช้งาน' }}
          </v-list-item-title>
          <v-list-item-subtitle class="user-details">
            {{ user.user_status }} : {{ user.user_firstname }} {{ user.user_lastname }}
          </v-list-item-subtitle>
          <v-chip 
            x-small 
            :color="user.status === 'online' ? 'success' : 'grey'" 
            class="mt-1 status-chip"
          >
            <v-icon left x-small>
              {{ user.status === 'online' ? 'mdi-circle' : 'mdi-circle-outline' }}
            </v-icon>
            {{ user.status === 'online' ? 'ออนไลน์' : 'ออฟไลน์' }}
          </v-chip>
        </v-list-item-content>

        <v-list-item-action v-if="!miniVariant">
          <v-btn 
            icon 
            small 
            @click="toggleMiniVariant"
            class="mini-toggle-btn"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </div>

    <v-divider class="header-divider" />

    <!-- Quick Actions (when mini) -->
    <div v-if="miniVariant" class="quick-actions">
      <v-tooltip right>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            class="quick-action-btn"
            to="/admin/Dashboard"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-view-dashboard</v-icon>
          </v-btn>
        </template>
        <span>แดชบอร์ด</span>
      </v-tooltip>

      <v-tooltip right>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            class="quick-action-btn"
            @click="toggleMiniVariant"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>
        <span>ขยายเมนู</span>
      </v-tooltip>
    </div>

    <!-- Main Navigation -->
    <div class="navigation-content">
      <!-- Search (when not mini) -->
      <div v-if="!miniVariant" class="search-section">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="ค้นหาเมนู"
          hide-details
          dense
          outlined
          clearable
          class="search-field"
        />
      </div>

      <!-- Navigation Items -->
      <v-list class="navigation-list" nav>
        <!-- Filtered Menu Items -->
        <div v-for="item in filteredMenuItems" :key="item.title">
          <v-list-group
            v-if="item.items && item.items.length > 0"
            :value="item.active"
            :prepend-icon="item.action"
            no-action
            class="menu-group"
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="menu-title">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.badge">
                <v-chip x-small :color="item.badgeColor || 'primary'">
                  {{ item.badge }}
                </v-chip>
              </v-list-item-action>
            </template>

            <v-list-item
              v-for="child in item.items"
              :key="child.title"
              :to="child.to"
              class="menu-item"
              exact
            >
              <v-list-item-icon class="menu-item-icon">
                <v-icon>{{ child.action }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="menu-item-title">
                  {{ child.title }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="child.badge">
                <v-chip x-small :color="child.badgeColor || 'info'">
                  {{ child.badge }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list-group>

          <!-- Single Menu Item (no children) -->
          <v-list-item
            v-else
            :to="item.to"
            class="single-menu-item"
            exact
          >
            <v-list-item-icon>
              <v-icon>{{ item.action }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="item.badge">
              <v-chip x-small :color="item.badgeColor || 'primary'">
                {{ item.badge }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </div>

        <!-- No Results Message -->
        <v-list-item v-if="filteredMenuItems.length === 0 && searchQuery">
          <v-list-item-content class="text-center">
            <v-icon size="48" color="grey lighten-2" class="mb-2">mdi-magnify</v-icon>
            <v-list-item-title class="grey--text">
              ไม่พบเมนูที่ค้นหา
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>

    <!-- Footer Section -->
    <template v-slot:append>
      <div class="drawer-footer" v-if="!miniVariant">
        <v-divider class="mb-2" />
        
        <!-- Settings & Logout -->
        <v-list dense>
          <v-list-item @click="goToSettings" class="footer-item">
            <v-list-item-icon>
              <v-icon>mdi-cog</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>ตั้งค่า</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          
          <v-list-item @click="toggleTheme" class="footer-item">
            <v-list-item-icon>
              <v-icon>{{ isDark ? 'mdi-brightness-7' : 'mdi-brightness-4' }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ isDark ? 'โหมดสว่าง' : 'โหมดมืด' }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          
          <v-list-item @click="logout" class="footer-item logout-item">
            <v-list-item-icon>
              <v-icon color="error">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="error--text">ออกจากระบบ</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Version Info -->
        <div class="version-info">
          <v-chip x-small outlined>
            v{{ appVersion }}
          </v-chip>
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
// Utilities
import { mapState } from "vuex";

export default {
  name: "DashboardUserDrawer",
  
  props: {
    expandOnHover: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      user: {},
      searchQuery: "",
      miniVariant: false,
      isDark: false,
      appVersion: "2.1.0",
      
      // Enhanced menu structure with better organization
      menuItems: [
        {
          title: "แดshboard & ระบบ",
          action: "mdi-view-dashboard",
          authority: "userauthorityAdmin",
          items: [
            {
              title: "แผงควบคุม",
              action: "mdi-chart-multiple",
              to: "/admin/Dashboard",
              badge: "หลัก"
            },
            {
              title: "ตั้งค่าระบบ",
              action: "mdi-cog",
              to: "/admin/setting"
            }
          ]
        },
        {
          title: "ผู้ใช้งานระบบ",
          action: "mdi-account-group",
          authority: "userauthorityAdmin",
          items: [
            {
              title: "ครูและบุคลากร",
              action: "mdi-account-school",
              to: "/admin/personnel"
            },
            {
              title: "วิทยาลัย สอจ.สถาบัน กจ2",
              action: "mdi-school",
              to: "/admin/user"
            },
            {
              title: "สถานะผู้ใช้งานระบบ",
              action: "mdi-account-cog",
              to: "/admin/user_status"
            }
          ]
        },
        {
          title: "ข้อมูลสถานศึกษา",
          action: "mdi-school",
          authority: "userauthorityAdmin",
          items: [
            {
              title: "ประเภทสถานศึกษา",
              action: "mdi-office-building",
              to: "/admin/collegetype"
            },
            {
              title: "ข้อมูลสถานศึกษา",
              action: "mdi-domain",
              to: "/admin/college"
            },
            {
              title: "รายละเอียดสถานศึกษา",
              action: "mdi-information",
              to: "/admin/collegeinfo"
            }
          ]
        },
        {
          title: "ระบบงานบุคคล",
          action: "mdi-account-tie",
          authority: "userauthorityHR",
          items: [
            {
              title: "ข้อมูลบุคคล",
              action: "mdi-account",
              to: "/admin/personnel_tem"
            },
            {
              title: "ขอย้ายบุคคล",
              action: "mdi-account-switch",
              to: "/admin/collegepersonnelreq"
            },
            {
              title: "คุณวุฒิการศึกษา",
              action: "mdi-school",
              to: "/admin/personnel_education"
            },
            {
              title: "ประสบการณ์",
              action: "mdi-briefcase",
              to: "/admin/personnel_experience"
            },
            {
              title: "ประวัติการรับราชการ",
              action: "mdi-history",
              to: "/admin/personnel_work_history"
            },
            {
              title: "ผลงาน รางวัล",
              action: "mdi-trophy",
              to: "/admin/personnel_award"
            },
            {
              title: "โทษ วินัย",
              action: "mdi-gavel",
              to: "/admin/personnel_discipline"
            }
          ]
        },
        {
          title: "ระบบการย้าย (ครู)",
          action: "mdi-account-switch",
          authority: "userauthorityMoveTeach",       
          badgeColor: "success",
          items: [
            {
              title: "สายการสอนและสนับสนุน",
              action: "mdi-account-arrow-right",
              to: "/admin/transference_personnel"
            },
            {
              title: "สายงานบริหารสถานศึกษา",
              action: "mdi-account-supervisor",
              to: "/admin/transference_manage"
            },
            {
              title: "การย้ายกรณีพิเศษ",
              action: "mdi-account-star",
              to: "/admin/transference_special"
            }
          ]
        },
        {
          title: "ระบบการย้าย (บริหาร)",
          action: "mdi-account-supervisor-circle",
          authority: "userauthorityMoveManage",
          items: [
            {
              title: "สายการสอนและสนับสนุน",
              action: "mdi-account-arrow-right",
              to: "/admin/transference_personnel_manage"
            },
            {
              title: "สายงานบริหารสถานศึกษา",
              action: "mdi-account-supervisor",
              to: "/admin/transference_manage_admin"
            },
            {
              title: "การย้ายกรณีพิเศษ",
              action: "mdi-account-star",
              to: "/admin/transference_special_manage"
            }
          ]
        },
        {
          title: "ระบบเงื่อนไขสาขาวิชา",
          action: "mdi-school-outline",
          authority: "userauthorityMoveTeach",
          items: [
            {
              title: "สาขาวิชาเอก",
              action: "mdi-book-open",
              to: "/admin/branch"
            },
            {
              title: "เงื่อนไขสาขาวิชาเอก",
              action: "mdi-file-document",
              to: "/admin/conditions_transfer"
            },
            {
              title: "สถิติเงื่อนไขสาขาวิชา",
              action: "mdi-chart-box",
              to: "/admin/conditions_statistics"
            }
          ]
        },
        {
          title: "ระบบอัตรากำลัง",
          action: "mdi-account-multiple",
          authority: "userauthorityManpower",
          items: [
            {
              title: "อัตรากำลังสถานศึกษา",
              action: "mdi-account-group",
              to: "/admin/man_power"
            },
            {
              title: "วิเคราะห์อัตรากำลัง",
              action: "mdi-chart-line",
              to: "/admin/man_power_analysis"
            }
          ]
        },
        {
          title: "ระบบครูผู้ช่วย",
          action: "mdi-account-school",
          authority: "userauthorityAssitantTeach",
          items: [
            {
              title: "รายงานตัวครูผู้ช่วย",
              action: "mdi-account-plus",
              to: "/admin/assistant_teacher_report/manage_assistant_teacher"
            },
            {
              title: "ตำแหน่งรับรายงานตัว",
              action: "mdi-map-marker",
              to: "/admin/assistant_teacher_report/manage_college_manpower"
            },
            {
              title: "การเลือกสถานศึกษา",
              action: "mdi-school-outline",
              to: "/admin/assistant_teacher_report/teacher_college_manpower_report"
            },
            {
              title: "การรายงานตัว ณ สถานศึกษา",
              action: "mdi-clipboard-check",
              to: "/admin/assistant_teacher_report/manage_assistant_CollegeReport"
            },
            {
              title: "การประเมินครูผู้ช่วย",
              action: "mdi-star",
              to: "/admin/assistant_teacher"
            }
          ]
        },
        {
          title: "ข่าวสาร & คู่มือ",
          action: "mdi-newspaper",
          authority: "all",
          items: [
            {
              title: "ข่าวสาร",
              action: "mdi-newspaper",
              to: "/admin/news_s",             
              badgeColor: "error"
            },
            {
              title: "คู่มือใช้งาน",
              action: "mdi-book",
              to: "/admin/manual_s"
            },            
          ]
        }
      ]
    };
  },

  computed: {
    ...mapState(["barColor", "barImage"]),
    
    drawer: {
      get() {
        return this.$store.state.drawer;
      },
      set(val) {
        this.$store.commit("SET_DRAWER", val);
      }
    },

    filteredMenuItems() {
      const userAuthorities = this.getUserAuthorities();
      let items = this.menuItems.filter(item => 
        item.authority === "all" || userAuthorities.includes(item.authority)
      );

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        items = items.filter(item => {
          // Search in main title
          if (item.title.toLowerCase().includes(query)) {
            return true;
          }
          // Search in sub-items
          if (item.items) {
            return item.items.some(subItem => 
              subItem.title.toLowerCase().includes(query)
            );
          }
          return false;
        }).map(item => ({
          ...item,
          items: item.items ? item.items.filter(subItem =>
            subItem.title.toLowerCase().includes(query) ||
            item.title.toLowerCase().includes(query)
          ) : []
        }));
      }

      return items;
    }
  },

  async mounted() {
    document.documentElement.classList.toggle("no-grayscale");
    await this.loadUserData();
    this.isDark = this.$vuetify.theme.dark;
  },

  methods: {
    async loadUserData() {
      try {
        const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        if (userSession.user_name) {
          const result = await this.$http.post("admin.php", {
            user_name: userSession.user_name,
            ApiKey: "HRvec2021"
          });
          
          this.user = {
            ...result.data,
            status: "online" // You can implement real status tracking
          };

          // Handle first-time user update
          if (this.user.user_update === "0") {
            this.user.ApiKey = "HRvec2021";
            this.user.user_update = 1;
            await this.$http.post("user.update.php", this.user);
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    },

    getUserAuthorities() {
      const authorities = [];
      if (this.user.userauthorityAdmin === "1") authorities.push("userauthorityAdmin");
      if (this.user.userauthorityAssitantTeach === "1") authorities.push("userauthorityAssitantTeach");
      if (this.user.userauthorityManpower === "1") authorities.push("userauthorityManpower");
      if (this.user.userauthorityMoveManage === "1") authorities.push("userauthorityMoveManage");
      if (this.user.userauthorityMoveTeach === "1") authorities.push("userauthorityMoveTeach");
      if (this.user.userauthorityHR === "1") authorities.push("userauthorityHR");
      return authorities;
    },

    toggleMiniVariant() {
      this.miniVariant = !this.miniVariant;
      // Save preference
      localStorage.setItem('miniVariant', this.miniVariant);
    },

    toggleTheme() {
      this.isDark = !this.isDark;
      this.$vuetify.theme.dark = this.isDark;
      localStorage.setItem('darkTheme', this.isDark);
    },

    goToSettings() {
      this.$router.push('/admin/setting');
    },

    logout() {
      this.$swal.fire({
        title: 'ยืนยันการออกจากระบบ',
        text: 'คุณต้องการออกจากระบบหรือไม่?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f44336',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'ออกจากระบบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          sessionStorage.clear();
          localStorage.removeItem('authToken');
          this.$router.push("/");
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.modern-drawer {
  border-right: 1px solid rgba(0, 0, 0, 0.05) !important;

  &.theme--dark {
    background: linear-gradient(180deg, #2d2d2d 0%, #1e1e1e 100%) !important;
    border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
}

.drawer-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 0;

  .user-profile-section {
    padding: 16px;
  }

  .user-avatar {
    border: 3px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      border-color: rgba(255, 255, 255, 0.6);
    }
  }

  .user-name {
    font-weight: 600;
    font-size: 1.1rem;
    color: white !important;
  }

  .user-details {
    color: rgba(255, 255, 255, 0.9) !important;
    font-size: 0.85rem;
  }

  .status-chip {
    background: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
  }

  .mini-toggle-btn {
    background: rgba(255, 255, 255, 0.1);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.header-divider {
  background: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%);
  height: 2px;
  border: none;
}

.quick-actions {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .quick-action-btn {
    background: rgba(103, 126, 234, 0.1);
    
    &:hover {
      background: rgba(103, 126, 234, 0.2);
      transform: translateX(4px);
    }
  }
}

.navigation-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;

  .search-section {
    padding: 16px;

    .search-field >>> .v-input__control {
      border-radius: 25px;
    }
  }

  .navigation-list {
    padding: 8px;

    .menu-group {
      margin-bottom: 4px;
      border-radius: 12px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(103, 126, 234, 0.05);
      }

      >>> .v-list-group__header {
        padding: 12px 16px;
        border-radius: 12px;
        
        &:hover {
          background: rgba(103, 126, 234, 0.1);
        }

        &.v-list-item--active {
          background: rgba(103, 126, 234, 0.15);
          color: #667eea;

          .v-icon {
            color: #667eea;
          }
        }
      }

      .menu-title {
        font-weight: 500;
        font-size: 0.95rem;
      }
    }

    .menu-item {
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(103, 126, 234, 0.08);
        transform: translateX(4px);
      }

      &.v-list-item--active {
        background: linear-gradient(90deg, rgba(103, 126, 234, 0.2) 0%, rgba(103, 126, 234, 0.1) 100%);
        border-left: 3px solid #667eea;
        color: #667eea;

        .v-icon {
          color: #667eea;
        }
      }

      .menu-item-icon {
        margin-right: 2px;
        min-width: 32px;
      }

      .menu-item-title {
        font-size: 0.9rem;
      }
    }

    .single-menu-item {
      border-radius: 12px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(103, 126, 234, 0.1);
        transform: translateX(4px);
      }

      &.v-list-item--active {
        background: linear-gradient(90deg, rgba(103, 126, 234, 0.2) 0%, rgba(103, 126, 234, 0.1) 100%);
        border-left: 3px solid #667eea;
        color: #667eea;

        .v-icon {
          color: #667eea;
        }
      }
    }
  }
}

.drawer-footer {
  background: rgba(0, 0, 0, 0.02);
  padding: 16px 8px 8px;

  .footer-item {
    border-radius: 8px;
    margin: 2px 0;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      transform: translateX(4px);
    }

    &.logout-item:hover {
      background: rgba(244, 67, 54, 0.1);
    }
  }

  .version-info {
    text-align: center;
    padding: 8px;
    opacity: 0.7;
  }
}

// Custom scrollbar
.navigation-content::-webkit-scrollbar {
  width: 6px;
}

.navigation-content::-webkit-scrollbar-track {
  background: transparent;
}

.navigation-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}

// Dark theme adjustments
.theme--dark {
  .modern-drawer {
    .menu-group:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .menu-item:hover,
    .single-menu-item:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .footer-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  .navigation-content::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .modern-drawer {
    width: 100% !important;
  }

  .drawer-header {
    .user-profile-section {
      padding: 12px;
    }

    .user-avatar {
      width: 48px !important;
      height: 48px !important;
    }

    .user-name {
      font-size: 1rem;
    }

    .user-details {
      font-size: 0.8rem;
    }
  }

  .navigation-content {
    .search-section {
      padding: 12px;
    }

    .navigation-list {
      padding: 4px;

      .menu-group >>> .v-list-group__header {
        padding: 10px 12px;
      }

    
    }
  }

  .drawer-footer {
    padding: 12px 4px 4px;
  }
}

// Animation for badges
.v-chip {
  animation: subtle-pulse 3s infinite;
}

@keyframes subtle-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

// Hover effects for enhanced interactivity
.menu-group {
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(103, 126, 234, 0.1), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
}

// Enhanced focus states for accessibility
.v-list-item:focus {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

// Smooth transitions for all interactive elements
* {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

// Custom styles for mini variant
.v-navigation-drawer--mini-variant {
  .quick-actions {
    align-items: center;
  }

  .quick-action-btn {
    width: 40px;
    height: 40px;
  }
}

// Loading states
.loading-placeholder {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  height: 20px;
  margin: 8px 0;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Enhanced search field styling
.search-field {
  >>> .v-input__control {
    .v-input__slot {
      background: rgba(255, 255, 255, 0.9) !important;
      border: 1px solid rgba(103, 126, 234, 0.2) !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        border-color: rgba(103, 126, 234, 0.4) !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      &:focus-within {
        border-color: #667eea !important;
        box-shadow: 0 0 0 2px rgba(103, 126, 234, 0.2);
      }
    }
  }

  .theme--dark & {
    >>> .v-input__control .v-input__slot {
      background: rgba(255, 255, 255, 0.1) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
    }
  }
}

// Status indicators
.status-indicator {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid white;
  }

  &.online::after {
    background: #4caf50;
    animation: pulse-green 2s infinite;
  }

  &.offline::after {
    background: #9e9e9e;
  }
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

// Enhanced menu group arrows
.v-list-group {
  >>> .v-list-group__header {
    .v-list-group__header__append-icon {
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &.v-list-group__header--active {
      .v-list-group__header__append-icon {
        transform: rotate(180deg);
      }
    }
  }
}

// Improved badge styling
.v-chip {
  font-weight: 600;
  font-size: 0.75rem;
  height: 20px;
  
  &.v-chip--x-small {
    height: 18px;
    font-size: 0.7rem;
  }
}

// Enhanced error and warning states
.error-state {
  color: #f44336 !important;
  background: rgba(244, 67, 54, 0.1) !important;
}

.warning-state {
  color: #ff9800 !important;
  background: rgba(255, 152, 0, 0.1) !important;
}

.success-state {
  color: #4caf50 !important;
  background: rgba(76, 175, 80, 0.1) !important;
}

// Print styles
@media print {
  .modern-drawer {
    display: none !important;
  }
}

// High contrast mode support
@media (prefers-contrast: high) {
  .modern-drawer {
    border-right: 2px solid currentColor !important;
  }

  .menu-item:hover,
  .single-menu-item:hover {
    border: 2px solid currentColor;
  }

  .v-chip {
    border: 1px solid currentColor;
  }
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .menu-group::before {
    display: none;
  }
}

// Focus management for keyboard navigation
.v-list-item {
  &:focus-visible {
    outline: 2px solid #667eea;
    outline-offset: 2px;
    z-index: 1;
  }
}

// Enhanced mini variant styles
.v-navigation-drawer--mini-variant {
  .drawer-header {
    text-align: center;
    
    .user-profile-section {
      flex-direction: column;
      align-items: center;
    }
  }

  .navigation-list {
    .menu-group >>> .v-list-group__header {
      justify-content: center;
      
      .v-list-item__content {
        display: none;
      }
    }
  }
}

// Custom scrollbar for better UX
.modern-drawer {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

// Theme transition
.theme-transition {
  transition: background-color 0.3s ease, color 0.3s ease;
}

// Loading skeleton animation
.skeleton-loader {
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.1) 25%, 
    rgba(255, 255, 255, 0.2) 50%, 
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200% 100%;
  animation: skeleton 1.5s infinite;
}

@keyframes skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>